import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check, RotateCcw } from 'lucide-react';
import { trackPageView, trackEvent } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const questions = [
  { id: 1, question: "Kya bana rahe ho?", options: [
    { label: "Ghar (Residential)", value: "residential", icon: "🏠" },
    { label: "Dukaan / Office (Commercial)", value: "commercial", icon: "🏢" },
    { label: "Compound Wall / Boundary", value: "wall", icon: "🧱" },
    { label: "Slab / Roof", value: "slab", icon: "🏗" },
    { label: "Road / Bridge (Infrastructure)", value: "infra", icon: "🌉" },
  ]},
  { id: 2, question: "Kitni floors banani hain?", options: [
    { label: "1-2 Floors", value: "low", icon: "1️⃣" },
    { label: "3-5 Floors", value: "mid", icon: "3️⃣" },
    { label: "6+ Floors (High-rise)", value: "high", icon: "🏙" },
  ]},
  { id: 3, question: "Location kaisa hai?", options: [
    { label: "Normal / Dry Area", value: "normal", icon: "☀" },
    { label: "Coastal / Humid Area", value: "coastal", icon: "🌊" },
    { label: "Underground / Basement", value: "underground", icon: "⛏" },
    { label: "Extreme Weather Area", value: "extreme", icon: "🌧" },
  ]},
  { id: 4, question: "Budget priority kya hai?", options: [
    { label: "Best Quality (Budget flexible)", value: "quality", icon: "⭐" },
    { label: "Value for Money (Balanced)", value: "balanced", icon: "⚖" },
    { label: "Cost-Effective (Budget tight)", value: "economy", icon: "💰" },
  ]},
  { id: 5, question: "Koi special requirement hai?", options: [
    { label: "Waterproofing zaruri hai", value: "waterproof", icon: "💧" },
    { label: "Fast setting chahiye", value: "fast", icon: "⚡" },
    { label: "Eco-friendly preferred", value: "eco", icon: "🌿" },
    { label: "Koi special requirement nahi", value: "none", icon: "✅" },
  ]},
];

const getRecommendation = (answers) => {
  const { 1: project, 2: floors, 3: location, 4: budget, 5: special } = answers;
  
  if (project === 'infra' || floors === 'high') {
    return { product: 'Lion Classic', grade: 'Composite Cement (IS 16415:2015)', reason: 'High-rise aur infrastructure ke liye maximum strength aur durability chahiye. Lion Classic composite cement 58+ MPa strength deta hai — perfect for demanding projects.', color: '#E85A1C', link: '/products/lion-classic' };
  }
  if (location === 'coastal' || location === 'underground' || special === 'waterproof') {
    return { product: 'Hathi Cement PPC', grade: 'PPC (IS 1489 Part 1:2015)', reason: 'Coastal, underground, ya waterproofing need ke liye PPC best hai. Hathi PPC mein 25-30% pozzolanic materials hain jo chemical attacks se protect karte hain aur long-term durability badhate hain.', color: '#1E4D8C', link: '/products/hathi-cement' };
  }
  if (special === 'eco') {
    return { product: 'Lion Classic', grade: 'Composite Cement (IS 16415:2015)', reason: 'Eco-friendly construction ke liye composite cement best choice hai — 40% kam CO2 emissions, same strength. Lion Classic IS 16415 certified hai.', color: '#E85A1C', link: '/products/lion-classic' };
  }
  if (budget === 'economy') {
    return { product: 'Siddhee Cement', grade: 'OPC 43 (IS 8112:2013)', reason: 'Budget-friendly par quality mein koi compromise nahi. Siddhee OPC 43 residential construction, plastering, aur general use ke liye perfect hai.', color: '#2E7D32', link: '/products/siddhee-cement' };
  }
  if (project === 'slab' || special === 'fast' || floors === 'mid') {
    return { product: 'Hathi Cement OPC 53', grade: 'OPC 53 (IS 12269:2013)', reason: 'Slab work aur multi-storey buildings ke liye OPC 53 grade ideal hai. Fast setting aur high early strength milti hai — 53+ MPa at 28 days.', color: '#1E4D8C', link: '/products/hathi-cement' };
  }
  return { product: 'Lion Supreme', grade: 'Premium PPC (IS 1489)', reason: 'Standard residential construction ke liye Lion Supreme perfect balance hai — premium quality, reliable strength, aur excellent finish. 20+ saal se Gujarat ke builders ka trusted choice.', color: '#D4AF37', link: '/products/lion-supreme' };
};

const QuizPage = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  useEffect(() => { trackPageView('/quiz', 'Product Recommendation Quiz'); }, []);

  const handleAnswer = (qId, value) => {
    const next = { ...answers, [qId]: value };
    setAnswers(next);
    if (step < questions.length - 1) {
      setTimeout(() => setStep(step + 1), 300);
    } else {
      const rec = getRecommendation(next);
      setResult(rec);
      trackEvent('quiz_complete', 'quiz', { recommended: rec.product });
    }
  };

  const reset = () => { setStep(0); setAnswers({}); setResult(null); };
  const q = questions[step];

  if (result) return (
    <div className="min-h-screen bg-[#0a0a0b] pt-28 pb-20 px-4" data-testid="quiz-page">
      <SEOHead
        title="Construction Quiz"
        description="Test your construction knowledge with our interactive quiz. Learn about cement grades, mixing ratios, AAC blocks, and building techniques."
        path="/quiz"
        noindex
      />
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="max-w-lg mx-auto text-center">
        <span className="text-[#E85A1C] text-xs font-semibold tracking-[0.2em] uppercase">Recommendation</span>
        <h2 className="text-3xl font-bold text-white mt-3 mb-2">Aapke Liye Best Hai</h2>
        
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}
          className="mt-8 bg-[#111113] border border-gray-800 rounded-2xl p-6 md:p-8 text-left" data-testid="quiz-result">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${result.color}20` }}>
              <Check className="w-7 h-7" style={{ color: result.color }} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">{result.product}</h3>
              <p className="text-gray-400 text-sm">{result.grade}</p>
            </div>
          </div>
          <p className="text-gray-300 text-sm leading-relaxed mb-6">{result.reason}</p>
          <div className="flex gap-3 flex-wrap">
            <Link to={result.link} data-testid="quiz-view-product"
              className="flex items-center gap-2 bg-[#E85A1C] hover:bg-[#d14e15] text-white px-6 py-3 rounded-xl font-medium text-sm transition-all">
              View Product <ArrowRight className="w-4 h-4" />
            </Link>
            <button onClick={reset} data-testid="quiz-retake"
              className="flex items-center gap-2 bg-[#111113] border border-gray-700 text-gray-300 hover:text-white px-6 py-3 rounded-xl text-sm transition-all">
              <RotateCcw className="w-4 h-4" /> Retake Quiz
            </button>
          </div>
        </motion.div>

        <div className="mt-6">
          <a href={`https://wa.me/918140044100?text=Hi, quiz ne mujhe ${result.product} (${result.grade}) recommend kiya. Iske baare mein details chahiye.`}
            target="_blank" rel="noopener noreferrer" data-testid="quiz-whatsapp"
            className="text-emerald-400 hover:text-emerald-300 text-sm">
            WhatsApp pe puchein &rarr;
          </a>
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0b] pt-28 pb-20 px-4" data-testid="quiz-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-xl mx-auto text-center mb-10">
        <span className="text-[#E85A1C] text-xs font-semibold tracking-[0.2em] uppercase">Product Quiz</span>
        <h1 className="text-3xl sm:text-4xl font-bold text-white mt-3 mb-4">
          Aapke Project ke Liye <span className="text-[#F5A623]">Sahi Cement</span>
        </h1>
        <p className="text-gray-400 text-sm">5 sawaal — 30 second — perfect recommendation.</p>
      </motion.div>

      {/* Progress */}
      <div className="max-w-xl mx-auto mb-8">
        <div className="flex gap-1.5">
          {questions.map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full transition-all ${i <= step ? 'bg-[#E85A1C]' : 'bg-gray-800'}`} />
          ))}
        </div>
        <p className="text-gray-500 text-xs mt-2">Question {step + 1} of {questions.length}</p>
      </div>

      {/* Question */}
      <motion.div key={q.id} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} className="max-w-xl mx-auto">
        <h2 className="text-xl font-bold text-white mb-6" data-testid="quiz-question">{q.question}</h2>
        <div className="space-y-3">
          {q.options.map(opt => (
            <button key={opt.value} onClick={() => handleAnswer(q.id, opt.value)}
              data-testid={`quiz-option-${opt.value}`}
              className={`w-full text-left p-4 rounded-xl border transition-all flex items-center gap-4 ${
                answers[q.id] === opt.value ? 'bg-[#E85A1C]/10 border-[#E85A1C]/30 text-white' : 'bg-[#111113] border-gray-800 text-gray-300 hover:border-gray-700 hover:text-white'
              }`}>
              <span className="text-xl">{opt.icon}</span>
              <span className="text-sm font-medium">{opt.label}</span>
            </button>
          ))}
        </div>

        {step > 0 && (
          <button onClick={() => setStep(step - 1)} className="flex items-center gap-2 text-gray-500 hover:text-white text-sm mt-6 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Previous
          </button>
        )}
      </motion.div>
    </div>
  );
};

export default QuizPage;
